import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormSection } from '@/components/forms/Form';
import { FormField, FormSelect, FormTextarea } from '@/components/forms/FormField';
import { useUpsertFormateur } from '@/hooks/queries/useFormation';
import { useAllSousSegments } from '@/hooks/queries/usePerimetres';
import type { FormateurRow, FormateurStatut } from '@/lib/database.types';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: FormateurRow | null;
  onSaved: () => void;
}

export function FormateurFormDialog({ open, onOpenChange, editing, onSaved }: Props): JSX.Element {
  const upsert = useUpsertFormateur();
  const { data: sousSegments = [] } = useAllSousSegments();

  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [bio, setBio] = useState('');
  const [statut, setStatut] = useState<FormateurStatut>('externe');
  const [tarifJournee, setTarifJournee] = useState('');
  const [tarifHoraire, setTarifHoraire] = useState('');
  const [specialites, setSpecialites] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setNom(editing.nom);
      setPrenom(editing.prenom);
      setEmail(editing.email ?? '');
      setTelephone(editing.telephone ?? '');
      setBio(editing.bio ?? '');
      setStatut(editing.statut);
      setTarifJournee(editing.tarif_journee?.toString() ?? '');
      setTarifHoraire(editing.tarif_horaire?.toString() ?? '');
      setSpecialites(editing.specialites_sous_segments ?? []);
    } else {
      setNom('');
      setPrenom('');
      setEmail('');
      setTelephone('');
      setBio('');
      setStatut('externe');
      setTarifJournee('');
      setTarifHoraire('');
      setSpecialites([]);
    }
    setError(null);
  }, [open, editing]);

  const toggleSpec = (id: string): void => {
    setSpecialites((cur) => (cur.includes(id) ? cur.filter((s) => s !== id) : [...cur, id]));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!nom.trim() || !prenom.trim()) {
      setError('Nom et prénom obligatoires');
      return;
    }
    try {
      await upsert.mutateAsync({
        id: editing?.id,
        nom: nom.trim(),
        prenom: prenom.trim(),
        email: email.trim() || null,
        telephone: telephone.trim() || null,
        bio: bio.trim() || null,
        statut,
        tarif_journee: tarifJournee ? Number(tarifJournee) : null,
        tarif_horaire: tarifHoraire ? Number(tarifHoraire) : null,
        specialites_sous_segments: specialites,
      });
      toast.success(editing ? 'Formateur mis à jour' : 'Formateur créé');
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    }
  };

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? 'Modifier le formateur' : 'Nouveau formateur'}
      onSubmit={(e) => void handleSubmit(e)}
      submitLabel={editing ? 'Enregistrer' : 'Créer'}
      isSubmitting={upsert.isPending}
    >
      {error && (
        <div className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}
      <FormSection title="Identité">
        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Prénom"
            required
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
          />
          <FormField label="Nom" required value={nom} onChange={(e) => setNom(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <FormField
            label="Téléphone"
            value={telephone}
            onChange={(e) => setTelephone(e.target.value)}
          />
        </div>
        <FormSelect
          label="Statut"
          value={statut}
          onChange={(v) => setStatut(v as FormateurStatut)}
          options={[
            { value: 'interne', label: 'Interne' },
            { value: 'externe', label: 'Externe' },
            { value: 'vacataire', label: 'Vacataire' },
          ]}
        />
        <FormTextarea
          label="Biographie"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={2}
        />
      </FormSection>

      <FormSection title="Tarification">
        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Tarif journée (DA)"
            type="number"
            value={tarifJournee}
            onChange={(e) => setTarifJournee(e.target.value)}
          />
          <FormField
            label="Tarif horaire (DA)"
            type="number"
            value={tarifHoraire}
            onChange={(e) => setTarifHoraire(e.target.value)}
          />
        </div>
      </FormSection>

      <FormSection title="Spécialités (sous-segments)">
        <div className="flex flex-wrap gap-1.5">
          {sousSegments.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => toggleSpec(s.id)}
              className={
                'rounded border px-2 py-0.5 text-xs ' +
                (specialites.includes(s.id)
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-input bg-background text-muted-foreground hover:bg-accent')
              }
            >
              {s.nom}
            </button>
          ))}
        </div>
      </FormSection>
    </FormDialog>
  );
}
